import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, Phone, Video, MessageSquare, MoreVertical } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const PAGE_SIZE = 12;

export function DashboardCustomers() {
  const { user, hasRole } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['customers', page, search],
    queryFn: async () => {
      const from = (page - 1) * PAGE_SIZE;
      let query = supabase
        .from('leads')
        .select('id, name, email, phone, status, source, created_at, property_id', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, from + PAGE_SIZE - 1);

      if (!hasRole('superadmin') && user) {
        query = query.or(`agent_id.eq.${user.id}`);
      }

      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,phone.ilike.%${search}%,email.ilike.%${search}%`);
      }

      const { data: leads, count, error } = await query;
      if (error) throw error;
      return { leads: leads || [], total: count || 0 };
    },
    enabled: !!user,
  });

  const leads = data?.leads || [];
  const totalCount = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleCall = (phone: string) => {
    if (phone) window.open(`tel:${phone}`);
    else toast.error('No phone number available');
  };

  const handleMessage = (phone: string) => {
    if (phone) window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}`);
    else toast.error('No phone number available');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-52 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'contacted': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'converted': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'closed': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customer..."
            className="pl-9 bg-muted/50 border-0 h-9 rounded-lg"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {totalCount} total leads
        </div>
      </div>

      {/* Customer Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {leads.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">No customers yet</div>
        ) : leads.map((lead: any) => (
          <Card key={lead.id} className="rounded-2xl border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5 flex flex-col items-center text-center">
              <div className="flex items-center justify-between w-full mb-3">
                <div className="relative">
                  <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center text-lg font-bold text-muted-foreground">
                    {lead.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-card ${lead.status === 'new' ? 'bg-primary' : lead.status === 'converted' ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                </div>
                <button className="text-muted-foreground self-start"><MoreVertical className="h-4 w-4" /></button>
              </div>
              <h4 className="font-semibold text-sm">{lead.name}</h4>
              <p className="text-[10px] text-primary font-semibold mt-0.5">#{lead.id?.slice(0, 6)}</p>
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full mt-1 uppercase ${statusColor(lead.status)}`}>
                {lead.status || 'new'}
              </span>
              <div className="flex gap-3 mt-3">
                <button
                  className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20"
                  onClick={() => handleCall(lead.phone)}
                  title="Call"
                >
                  <Phone className="h-3.5 w-3.5" />
                </button>
                <button
                  className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20"
                  onClick={() => handleMessage(lead.phone)}
                  title="WhatsApp"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                </button>
                {lead.email && (
                  <button
                    className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20"
                    onClick={() => window.open(`mailto:${lead.email}`)}
                    title="Email"
                  >
                    <Video className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="mt-3 text-[10px] text-muted-foreground w-full">
                <div className="flex justify-between">
                  <span className="font-medium text-foreground">Phone</span>
                  <span>{lead.phone || 'N/A'}</span>
                </div>
                {lead.email && (
                  <div className="flex justify-between mt-0.5">
                    <span className="font-medium text-foreground">Email</span>
                    <span className="truncate max-w-[120px]">{lead.email}</span>
                  </div>
                )}
                <div className="flex justify-between mt-0.5">
                  <span className="font-medium text-foreground">Source</span>
                  <span>{lead.source || 'website'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground flex items-center px-2">
            Page {page} of {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
